import React, { useState } from 'react';
import './Portfolio.css';
import PortfolioDetail from '../PortfolioDetail/PortfolioDetail';
import OptimizedImage from '../OptimizedImage/OptimizedImage';

interface PortfolioItem {
  id: number;
  title: string;
  category: string;
  location: string;
  image: string;
  images?: string[];
  description: string;
  details: {
    year: string;
    area: string;
    client: string;
  };
  size: string;
  offsetY: string;
}

const Portfolio: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<PortfolioItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const portfolioItems: PortfolioItem[] = [
    {
      id: 1,
      title: "Meruya Boarding House",
      category: "Residential-Commercial",
      location: "Jakarta",
      image: "/assets/images/webp/kost_menara/kost_menara_5_compressed.webp",
      images: [
        "/assets/images/webp/kost_menara/kost_menara_1_compressed.webp",
        "/assets/images/webp/kost_menara/kost_menara_4_compressed.webp",
        "/assets/images/webp/kost_menara/kost_menara_3_compressed.webp",
        "/assets/images/webp/kost_menara/kost_menara_2_compressed.webp"
      ],
      description: "Six-floor boarding house with a modern minimalist design. Japandi-inspired interiors with warm wood tones create an apartment-like feel. Neutral colors and soft textures make the space inviting, giving residents a comfortable and stylish place to live.",
      details: {
        year: "2025",
        area: "1100 m²",
        client: "Private Developer"
      },
      size: "xl",
      offsetY: "up"
    },
    {
      id: 2,
      title: "IEVOKE",
      category: "Commercial",
      location: "Bandung",
      image: "/assets/images/webp/ievoke/ievoke_2_compressed.webp",
      images: [
        "/assets/images/webp/ievoke/ievoke_1_compressed.webp",
        "/assets/images/webp/ievoke/ievoke_4_compressed.webp",
        "/assets/images/webp/ievoke/ievoke_3_compressed.webp",
        "/assets/images/webp/ievoke/ievoke_2_compressed.webp",
        "/assets/images/webp/ievoke/ievoke_5_compressed.webp",
        "/assets/images/webp/ievoke/ievoke_6_compressed.webp"
      ],
      description: "Four-floor sport center with boxing, pilates, yoga, and gym facilities. Designed with a natural concept and a classic touch, combining earthy colors and timeless details. The result is a serene yet sophisticated environment for both training and social interaction.",
      details: {
        year: "2025",
        area: "1300 m²",
        client: "IEVOKE Fitness"
      },
      size: "large",
      offsetY: "down"
    },
    {
      id: 3,
      title: "Eclipse",
      category: "Commercial",
      location: "Jakarta",
      image: "/assets/images/webp/eclipse/eclipse_1_compressed.webp",
      images: [
        "/assets/images/webp/eclipse/eclipse_1_compressed.webp",
        "/assets/images/webp/eclipse/eclipse_2_compressed.webp",
        "/assets/images/webp/eclipse/eclipse_3_compressed.webp",
        "/assets/images/webp/eclipse/eclipse_4_compressed.webp",
        "/assets/images/webp/eclipse/eclipse_5_compressed.webp"
      ],
      description: "Game center with a dark, futuristic look. RGB lighting sets an immersive and energetic atmosphere that enhances the gaming experience. Sleek finishes and bold forms give the space a modern, high-tech character.",
      details: {
        year: "2025",
        area: "236 m²",
        client: "Eclipse Gaming"
      },
      size: "medium",
      offsetY: "up"
    },
    {
      id: 4,
      title: "Northwood",
      category: "Commercial",
      location: "Bandung",
      image: "/assets/images/webp/northwood/northwood_4_compressed.webp",
      images: [
        "/assets/images/webp/northwood/northwood_1_compressed.webp",
        "/assets/images/webp/northwood/northwood_2_compressed.webp",
        "/assets/images/webp/northwood/northwood_3_compressed.webp",
        "/assets/images/webp/northwood/northwood_4_compressed.webp",
        "/assets/images/webp/northwood/northwood_5_compressed.webp"
      ],
      description: "An interior project with a focus on green and terracotta as the main palette, accented with elegant gold details. The design brings a natural yet sophisticated feel, creating a warm and refined interior atmosphere.",
      details: {
        year: "2025",
        area: "16 m²",
        client: "Northwood Cafe"
      },
      size: "medium",
      offsetY: "down"
    },
    {
      id: 5,
      title: "Springville",
      category: "Residential",
      location: "Bandung",
      image: "/assets/images/webp/springville_tki/springville_tki_1_compressed.webp",
      images: [
        "/assets/images/webp/springville_tki/springville_tki_1_compressed.webp",
        "/assets/images/webp/springville_tki/springville_tki_2_compressed.webp",
        "/assets/images/webp/springville_tki/springville_tki_3_compressed.webp",
        "/assets/images/webp/springville_tki/springville_tki_4_compressed.webp",
        "/assets/images/webp/springville_tki/springville_tki_5_compressed.webp"
      ],
      description: "Home gym with a semi-industrial style, featuring stucco walls and exposed brick for a cozy, warm atmosphere. Industrial lighting and a simple material palette make the space functional yet stylish, encouraging daily workouts.",
      details: {
        year: "2025",
        area: "50 m²",
        client: "Private Residence"
      },
      size: "large",
      offsetY: "center"
    },
    {
      id: 6,
      title: "Ultima Padel",
      category: "Commercial",
      location: "Bandung",
      image: "/assets/images/webp/ultima_padel/ultima_padel_4_compressed.webp",
      images: [
        "/assets/images/webp/ultima_padel/ultima_padel_1_compressed.webp",
        "/assets/images/webp/ultima_padel/ultima_padel_2_compressed.webp",
        "/assets/images/webp/ultima_padel/ultima_padel_3_compressed.webp",
        "/assets/images/webp/ultima_padel/ultima_padel_4_compressed.webp",
        "/assets/images/webp/ultima_padel/ultima_padel_5_compressed.webp",
        "/assets/images/webp/ultima_padel/ultima_padel_6_compressed.webp"
      ],
      description: "Modern padel court design with clean lines and contemporary materials. The layout prioritizes player flow and visibility, creating an engaging and premium sports experience for all visitors.",
      details: {
        year: "2025",
        area: "725 m²",
        client: "Ultima Sports"
      },
      size: "xl",
      offsetY: "center"
    },
    {
      id: 7,
      title: "Home Kitchen",
      category: "Residential",
      location: "Bandung",
      image: "/assets/images/webp/kitchen_m/kitchen_m_1_compressed.webp",
      images: [
        "/assets/images/webp/kitchen_m/kitchen_m_1_compressed.webp",
        "/assets/images/webp/kitchen_m/kitchen_m_2_compressed.webp",
        "/assets/images/webp/kitchen_m/kitchen_m_3_compressed.webp"
      ],
      description: "Japandi modern kitchen with medium-tone wood, creating a warm and inviting space. The clean layout and practical storage solutions make cooking more enjoyable and the kitchen a central gathering point.",
      details: {
        year: "2025",
        area: "20 m²",
        client: "Private Residence"
      },
      size: "medium",
      offsetY: "up"
    },
    {
      id: 8,
      title: "Cornel's",
      category: "Commercial",
      location: "Jakarta",
      image: "/assets/images/webp/cornels/cornels_6_compressed.webp",
      images: [
        "/assets/images/webp/cornels/cornels_6_compressed.webp",
        "/assets/images/webp/cornels/cornels_1_compressed.webp",
        "/assets/images/webp/cornels/cornels_2_compressed.webp",
        "/assets/images/webp/cornels/cornels_3_compressed.webp",
        "/assets/images/webp/cornels/cornels_4_compressed.webp",
        "/assets/images/webp/cornels/cornels_5_compressed.webp"
      ],
      description: "Contemporary modern restaurant with clean lines and a stylish yet welcoming ambiance. Thoughtful lighting and seating arrangements enhance the dining experience, making it comfortable for casual meals or special occasions.",
      details: {
        year: "2025",
        area: "935 m²",
        client: "Cornel's Restaurant"
      },
      size: "special-tall",
      offsetY: "center"
    }
  ];

  const openModal = (item: PortfolioItem) => {
    setSelectedProject(item);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const navigateProject = (direction: 'prev' | 'next') => {
    if (!selectedProject) return;

    const currentIndex = portfolioItems.findIndex(item => item.id === selectedProject.id);
    let newIndex;

    if (direction === 'prev') {
      newIndex = currentIndex === 0 ? portfolioItems.length - 1 : currentIndex - 1;
    } else {
      newIndex = currentIndex === portfolioItems.length - 1 ? 0 : currentIndex + 1;
    }

    setSelectedProject(portfolioItems[newIndex]);
  };

  return (
    <>
      <section id="portfolio" className="portofolio-main-wrapper">
        <div className="pmw-heading">
          <h1>Our <span id="gold">Portfolio</span></h1>
          <h4>
            Discover our collection of architectural masterpieces, each telling a unique story
            of innovation, sustainability, and <span id="gold">design excellence</span>.
          </h4>
        </div>

        <div className="pmw-content">
          <div className="content-grid">
            <div className="cathedral-masonry">
              {portfolioItems.map((item, index) => (
                <button
                  key={item.id}
                  className={`masonry-item ${item.size} offset-${item.offsetY} item-${index + 1}`}
                  onClick={() => openModal(item)}
                >
                  <div className="image-container">
                    <div className="image">
                      <OptimizedImage src={item.image} alt={item.title} loading="eager" />
                      <div className="slide-title">
                        <h4>{item.category.toUpperCase()} / {item.location.toUpperCase()}</h4>
                        <h2>{item.title}</h2>
                      </div>
                      <div className="image-info">
                        <div className="info-left">
                        </div>
                        <div className="info-right">
                          <div className="info-arrow">
                            <svg viewBox="0 0 24 24" fill="none">
                              <path d="m7 7 10 10M7 17l10-10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="back-top-button">
            <div className="top">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Detail Modal */}
      <PortfolioDetail
        item={selectedProject}
        isOpen={isModalOpen}
        onClose={closeModal}
        onNavigate={navigateProject}
      />
    </>
  );
};

export default Portfolio;